import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, Form, Modal, ProgressBar, Row } from 'react-bootstrap';
import { Pen, PlusCircle, Trash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator/FormGenerator';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import AlertModal from '../AlertModal';
import EntityTable from '../common/EntityTable';
import Loader from '../Loader';
import Tabs from '../common/Tabs';

const CATEGORY_TABS = ['Single Wide', 'Multi Wide'];

const getOptionForm = ({ type = 'Add', listTitle, submitPrefix }) => ({
  forms: [
    {
      name: `${type} ${listTitle}`,
      markCompulsoryFields: true,
      hideFormName: true,
      compact: true,
      submit: {
        name: type,
        show: true,
        onSubmit: type === 'Add' ? `on${submitPrefix}OptionAddSubmit` : `on${submitPrefix}OptionUpdateSubmit`
      },
      rows: [
        {
          columns: [
            {
              default: 6,
              field: {
                id: 'option',
                title: `${type === 'Add' ? 'New' : 'Update'} Option`,
                type: 'text',
                required: true
              }
            },
            {
              default: 6,
              field: {
                id: 'defaultValue',
                title: `${type === 'Add' ? 'New' : 'Update'} Default Value`,
                type: 'number',
                required: true
              }
            }
          ]
        }
      ]
    }
  ]
});

const AddEditModal = ({ show, onHide, showProgress, modalMeta }) => (
  <Modal show={show} onHide={onHide} centered backdrop="static">
    <Modal.Header closeButton={!showProgress}>
      <Modal.Title>
        <h6 className="mb-0">
          {modalMeta?.option ? `Update ${modalMeta.listTitle}` : `Add New ${modalMeta?.listTitle || 'Option'}`}
        </h6>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="overflow-auto px-1">
      <FormGenerator
        formJson={
          modalMeta?.option
            ? getOptionForm({ type: 'Update', listTitle: modalMeta.listTitle, submitPrefix: modalMeta.submitPrefix })
            : getOptionForm({ type: 'Add', listTitle: modalMeta?.listTitle, submitPrefix: modalMeta?.submitPrefix })
        }
        formValues={
          modalMeta?.option ? { [`Update ${modalMeta.listTitle}`]: modalMeta.option } : {}
        }
      />
      {showProgress && (
        <ProgressBar now={100} animated striped label={modalMeta?.option ? 'Updating option...' : 'Saving option...'} />
      )}
    </Modal.Body>
  </Modal>
);

const CommissionSheetOptions = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState(CATEGORY_TABS[0]);
  const [salesLocations, setSalesLocations] = useState([]);
  const [selectedSalesLocationId, setSelectedSalesLocationId] = useState('');
  const [records, setRecords] = useState([]);
  const [pnlRecords, setPnlRecords] = useState([]);
  const [modalMeta, setModalMeta] = useState(null);
  const [deleteMeta, setDeleteMeta] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const [locationsRes, commissionRes, pnlRes] = await Promise.all([
      makeApiRequests({ endpoint: ENDPOINTS.SALES_LOCATION_LIST, method: 'POST' }),
      makeApiRequests({ endpoint: ENDPOINTS.COMMISSION_SHEET_OPTIONS_LIST, method: 'POST' }),
      makeApiRequests({ endpoint: ENDPOINTS.PNL_OPTIONS_LIST, method: 'POST' })
    ]);
    setLoading(false);

    if (locationsRes.error) return toast.error(locationsRes.error);
    if (commissionRes.error) return toast.error(commissionRes.error);
    if (pnlRes.error) return toast.error(pnlRes.error);

    const locations = locationsRes.response || [];
    setSalesLocations(locations);
    setRecords(commissionRes.response || []);
    setPnlRecords(pnlRes.response || []);
    if (locations.length > 0 && !selectedSalesLocationId) {
      setSelectedSalesLocationId(locations[0]._id);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const activeRecord = useMemo(
    () =>
      records.find(
        record =>
          (record?.salesLocation?._id || record?.salesLocation) === selectedSalesLocationId &&
          record?.category === category
      ),
    [records, selectedSalesLocationId, category]
  );

  const activePnlRecord = useMemo(
    () =>
      pnlRecords.find(
        record =>
          (record?.salesLocation?._id || record?.salesLocation) === selectedSalesLocationId &&
          record?.category === category
      ),
    [pnlRecords, selectedSalesLocationId, category]
  );

  const getCommissionTableRows = fieldName =>
    (activeRecord?.[fieldName] || []).map(item => ({
      option: item.name,
      defaultValue: Number(item.defaultValue || 0)
    }));

  const getPnlTableRows = () =>
    (activePnlRecord?.pnlOptions || []).map(item => ({
      option: item.name,
      defaultValue: Number(item.defaultValue || 0)
    }));

  const upsertCommissionFieldValues = async (fieldName, values) => {
    if (!selectedSalesLocationId) return toast.error('Please select a sales location');
    setSaving(true);

    const formattedValues = values.map(v => ({
      name: v.option,
      defaultValue: Number(v.defaultValue || 0)
    }));

    const payload = {
      salesLocation: selectedSalesLocationId,
      category,
      commissionOptions: fieldName === 'commissionOptions' ? formattedValues : activeRecord?.commissionOptions || [],
      commissionLessOptions:
        fieldName === 'commissionLessOptions' ? formattedValues : activeRecord?.commissionLessOptions || []
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.COMMISSION_SHEET_OPTIONS_UPSERT,
      requestBody: payload,
      method: 'POST'
    });

    setSaving(false);
    if (error) return toast.error(error);

    const existingIndex = records.findIndex(r => r._id === response._id);
    if (existingIndex !== -1) {
      const updatedRecords = [...records];
      updatedRecords[existingIndex] = response;
      setRecords(updatedRecords);
    } else {
      setRecords([response, ...records]);
    }
    return true;
  };

  const upsertPnlFieldValues = async values => {
    if (!selectedSalesLocationId) return toast.error('Please select a sales location');
    setSaving(true);

    const formattedValues = values.map(v => ({
      name: v.option,
      defaultValue: Number(v.defaultValue || 0)
    }));

    const payload = {
      salesLocation: selectedSalesLocationId,
      category,
      pnlOptions: formattedValues
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.PNL_OPTIONS_UPSERT,
      requestBody: payload,
      method: 'POST'
    });

    setSaving(false);
    if (error) return toast.error(error);

    const existingIndex = pnlRecords.findIndex(r => r._id === response._id);
    if (existingIndex !== -1) {
      const updatedRecords = [...pnlRecords];
      updatedRecords[existingIndex] = response;
      setPnlRecords(updatedRecords);
    } else {
      setPnlRecords([response, ...pnlRecords]);
    }
    return true;
  };

  const handleCommissionAddSubmit = async form => {
    const existingOptions = getCommissionTableRows(modalMeta.fieldName);
    if (existingOptions.some(v => v.option === form.option)) {
      return toast.error('Option already added!');
    }

    const success = await upsertCommissionFieldValues(modalMeta.fieldName, [
      ...existingOptions,
      { option: form.option, defaultValue: Number(form.defaultValue || 0) }
    ]);
    if (!success) return;
    setModalMeta(null);
    toast.success('Option added!');
  };

  const handleCommissionUpdateSubmit = async form => {
    const existingOptions = getCommissionTableRows(modalMeta.fieldName);
    const updated = existingOptions.map(v =>
      v.option === modalMeta.option.option
        ? { option: form.option, defaultValue: Number(form.defaultValue || 0) }
        : v
    );
    if (updated.filter(v => v.option === form.option).length > 1) {
      return toast.error('Option already added!');
    }

    const success = await upsertCommissionFieldValues(modalMeta.fieldName, updated);
    if (!success) return;
    setModalMeta(null);
    toast.success('Option updated!');
  };

  const handlePnlAddSubmit = async form => {
    const existingOptions = getPnlTableRows();
    if (existingOptions.some(v => v.option === form.option)) {
      return toast.error('Option already added!');
    }

    const success = await upsertPnlFieldValues([
      ...existingOptions,
      { option: form.option, defaultValue: Number(form.defaultValue || 0) }
    ]);
    if (!success) return;
    setModalMeta(null);
    toast.success('Option added!');
  };

  const handlePnlUpdateSubmit = async form => {
    const existingOptions = getPnlTableRows();
    const updated = existingOptions.map(v =>
      v.option === modalMeta.option.option
        ? { option: form.option, defaultValue: Number(form.defaultValue || 0) }
        : v
    );
    if (updated.filter(v => v.option === form.option).length > 1) {
      return toast.error('Option already added!');
    }

    const success = await upsertPnlFieldValues(updated);
    if (!success) return;
    setModalMeta(null);
    toast.success('Option updated!');
  };

  const handleDelete = async () => {
    if (deleteMeta.type === 'pnl') {
      const existingOptions = getPnlTableRows();
      const updated = existingOptions.filter(v => v.option !== deleteMeta.option.option);
      const success = await upsertPnlFieldValues(updated);
      if (!success) return;
      setDeleteMeta(null);
      toast.success('Option deleted!');
      return;
    }

    const existingOptions = getCommissionTableRows(deleteMeta.fieldName);
    const updated = existingOptions.filter(v => v.option !== deleteMeta.option.option);
    const success = await upsertCommissionFieldValues(deleteMeta.fieldName, updated);
    if (!success) return;
    setDeleteMeta(null);
    toast.success('Option deleted!');
  };

  const onCommissionSort = async (fieldName, sortedList) => {
    const success = await upsertCommissionFieldValues(fieldName, sortedList);
    if (!success) return;
    toast.success('Options sorted!');
  };

  const onPnlSort = async sortedList => {
    const success = await upsertPnlFieldValues(sortedList);
    if (!success) return;
    toast.success('Options sorted!');
  };

  window.onCommissionOptionAddSubmit = handleCommissionAddSubmit;
  window.onCommissionOptionUpdateSubmit = handleCommissionUpdateSubmit;
  window.onPnlOptionAddSubmit = handlePnlAddSubmit;
  window.onPnlOptionUpdateSubmit = handlePnlUpdateSubmit;

  const renderCommissionTable = ({ title, fieldName }) => {
    const rows = getCommissionTableRows(fieldName);
    return (
      <Col lg={6} md={12}>
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">{title}</h6>
              <Button
                size="sm"
                variant="success"
                disabled={saving || !selectedSalesLocationId}
                onClick={() =>
                  setModalMeta({ type: 'commission', fieldName, listTitle: title, submitPrefix: 'Commission' })
                }
              >
                <PlusCircle className="mr-1" />
                Add Option
              </Button>
            </div>
            <EntityTable
              draggable
              onListSort={list => onCommissionSort(fieldName, list)}
              rowKeyField="option"
              data={rows}
              headers={[
                { name: 'Options', key: 'option' },
                { name: 'Default Value', key: 'defaultValue', type: 'number' }
              ]}
              searchQueryKeys={['option']}
              actionCell={option => (
                <div>
                  <Button
                    disabled={saving}
                    variant="outline-primary"
                    className="px-1 py-0 mr-1"
                    onClick={() =>
                      setModalMeta({ type: 'commission', fieldName, listTitle: title, submitPrefix: 'Commission', option })
                    }
                  >
                    <Pen size={10} />
                  </Button>
                  <Button
                    disabled={saving}
                    variant="outline-danger"
                    className="px-1 py-0"
                    onClick={() => setDeleteMeta({ type: 'commission', fieldName, listTitle: title, option })}
                  >
                    <Trash size={10} />
                  </Button>
                </div>
              )}
            />
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderPnlTable = () => {
    const rows = getPnlTableRows();
    return (
      <Col lg={6} md={12}>
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">P&L Options</h6>
              <Button
                size="sm"
                variant="success"
                disabled={saving || !selectedSalesLocationId}
                onClick={() => setModalMeta({ type: 'pnl', listTitle: 'P&L Option', submitPrefix: 'Pnl' })}
              >
                <PlusCircle className="mr-1" />
                Add Option
              </Button>
            </div>
            <EntityTable
              draggable
              onListSort={onPnlSort}
              rowKeyField="option"
              data={rows}
              headers={[
                { name: 'Options', key: 'option' },
                { name: 'Default Value', key: 'defaultValue', type: 'number' }
              ]}
              searchQueryKeys={['option']}
              actionCell={option => (
                <div>
                  <Button
                    disabled={saving}
                    variant="outline-primary"
                    className="px-1 py-0 mr-1"
                    onClick={() =>
                      setModalMeta({ type: 'pnl', listTitle: 'P&L Option', submitPrefix: 'Pnl', option })
                    }
                  >
                    <Pen size={10} />
                  </Button>
                  <Button
                    disabled={saving}
                    variant="outline-danger"
                    className="px-1 py-0"
                    onClick={() => setDeleteMeta({ type: 'pnl', listTitle: 'P&L Option', option })}
                  >
                    <Trash size={10} />
                  </Button>
                </div>
              )}
            />
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Card>
        <Card.Body>
          <h6>Commission/P&L options</h6>
          <hr className="my-3" />
          {loading ? (
            <Loader />
          ) : (
            <>
              <Row className="align-items-center">
                <Col md={8}>
                  <Tabs tabs={CATEGORY_TABS} activeTab={category} onTabSelect={setCategory} searchable={false} />
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-0 d-flex align-items-center justify-content-md-end">
                    <Form.Label className="small text-muted mb-0 mr-2 text-nowrap">Location:</Form.Label>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={selectedSalesLocationId}
                      onChange={e => setSelectedSalesLocationId(e.target.value)}
                    >
                      {salesLocations.map(location => (
                        <option key={location._id} value={location._id}>
                          {location.location || location.name || location.fullName}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              <hr />
              <Row>
                {renderCommissionTable({ title: 'Commission Options', fieldName: 'commissionOptions' })}
                {renderCommissionTable({ title: 'Commission Less Options', fieldName: 'commissionLessOptions' })}
                {renderPnlTable()}
              </Row>
            </>
          )}
        </Card.Body>
      </Card>

      <AlertModal
        show={deleteMeta !== null}
        alertText={`Are you sure you want to delete this option (${deleteMeta?.option?.option || ''})? This action cannot be undone!`}
        onHide={() => setDeleteMeta(null)}
        onDismissClick={() => setDeleteMeta(null)}
        onContinueClick={handleDelete}
        progressText="Deleting option..."
        showProgress={saving}
      />

      <AddEditModal
        show={modalMeta !== null}
        onHide={() => setModalMeta(null)}
        showProgress={saving}
        modalMeta={modalMeta}
      />
    </Container>
  );
};

export default CommissionSheetOptions;
